import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, CircularProgress } from '@mui/material';
import InfiniteScroll from 'react-infinite-scroller';
import { PaginatedResponse } from '../../interfaces/PaginatedResponse';
import { ProductSummary } from '../../interfaces/Product/ProductSummary';
import { getProductsByBrand } from '../../api/Product';
import {
  getDefaultProductFilters,
  ProductOrder,
} from '../../interfaces/Product/GetProductsRequest';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ProductGrid } from '../../components/ProductGrid/ProductGrid';
import { SortProductsHeader } from '../../components/SortProductsHeader/SortProductsHeader';
import { getEmptyMeta } from '../../interfaces/Meta/Meta';
import { getBrandMeta } from '../../api/Brand';
import { GenericHelmet } from '../../components/Helmet/GenericHelmet';
import { BreadcrumbElement } from '../../interfaces/Breadcrumb/BreadcrumbElement';
import { GenericBreadcrumbs } from '../../components/Breadcrumbs/GenericBreadcrumbs';
import { PopupSlot } from '../../components/Popup/PopupSlot';
import { useAnalytics } from '../../analytics/useAnalytics';
import { useLocalizedText } from '../../hooks/useLocalizedText';

export const BrandProductsPage = () => {
  const t = useLocalizedText();

  const { viewContent } = useAnalytics();

  const { slug } = useParams<string>();

  const [products, setProducts] = useState<PaginatedResponse<ProductSummary>>({
    data: [],
    totalCount: 0,
  });
  const [meta, setMeta] = useState(getEmptyMeta());
  const [order, setOrder] = useState<ProductOrder>('orderByQuantityDesc');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const isMobile = useIsMobile();

  useEffect(() => {
    if (!slug) return;

    setLoading(true);
    getProductsByBrand(slug, {
      ...getDefaultProductFilters(),
      order,
      page: 0,
    })
      .then(setProducts)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));

    getBrandMeta(slug)
      .then((m) => {
        setMeta({
          ...m,
          description: m.description || t('brand.products'),
          canonicalURL: m.canonicalURL || `marka/${slug}`,
        });
      })
      .catch((err) => toast.error(err.message));

    setPage(0);

    viewContent({ contentType: 'product_group', slug });
  }, [slug]);

  const incrementPage = useCallback(() => {
    if (loading || products.data.length >= products.totalCount) return;

    const nextPage = page + 1;
    setLoading(true);

    getProductsByBrand(slug ?? '', {
      ...getDefaultProductFilters(),
      order,
      page: nextPage,
    })
      .then((nextProducts) => {
        setProducts((prev) => ({
          data: [...prev.data, ...nextProducts.data],
          totalCount: nextProducts.totalCount,
        }));
        setPage(nextPage);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [loading, page, slug, products]);

  const handleSort = (newOrder: ProductOrder) => {
    setOrder(newOrder);
    setPage(0);
    setLoading(true);
    getProductsByBrand(slug ?? '', {
      ...getDefaultProductFilters(),
      order: newOrder,
      page: 0,
    })
      .then(setProducts)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const brandName =
    products.data[0]?.brandName || meta.title || slug || t('brand.products');

  const breadcrumbElements: BreadcrumbElement[] = [
    { name: t('brand.brands'), url: 'markalar' },
    { name: brandName, url: `marka/${slug}` },
  ];

  const resolvedLoading = loading && !products.data.length;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
        gap: 5,
      }}
    >
      <GenericHelmet
        title={brandName || t('brand.products')}
        meta={{ title: brandName, ...meta }}
        breadcrumb={breadcrumbElements}
      />

      <PopupSlot brandSlug={slug} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: 4.5,
          alignItems: 'center',
          boxSizing: 'border-box',
          px: isMobile ? 1.5 : 5,
          py: 2.5,
        }}
      >
        <GenericBreadcrumbs
          breadcrumbElements={breadcrumbElements}
          sx={{ mb: -3 }}
        />

        <SortProductsHeader
          loading={resolvedLoading}
          totalCount={products.totalCount}
          order={order}
          onChangeOrder={handleSort}
        />

        <InfiniteScroll
          pageStart={0}
          loadMore={incrementPage}
          hasMore={products.totalCount > products.data.length}
          loader={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <CircularProgress color='inherit' />
            </Box>
          }
          style={{ width: '100%' }}
        >
          <ProductGrid products={products.data} loading={resolvedLoading} />
        </InfiniteScroll>
      </Box>
    </Box>
  );
};
